//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
// import React, { useState, useEffect, useContext } from 'react';
import React, { useState, useCallback, useContext, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import SendIcon from '@material-ui/icons/Send';
import AttachmentIcon from '@material-ui/icons/Attachment';
import Tooltip from '@material-ui/core/Tooltip';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { THIS_THREAD_HAS_BEEN_DEACTIVATED } from '../../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdButton from '../../common/elemental/AfitdButton';
// import AfitdIconButton from '../../common/elemental/AfitdIconButton';
import AfitdTypography from '../../common/elemental/AfitdTypography';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import FileReplyDialog from './FileReplyDialog';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import { format } from 'date-fns';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _isNil from 'lodash/isNil';
import _trim from 'lodash/trim';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        root: {
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#FFFFFF',
            padding: '0px 24px 0px 0px',
        },
        blockMessageContainer: {
            marginLeft: '24px',
            height: '47px',
            display: 'flex',
            alignItems: 'center',
        },
        // chatRightBlock3a
        attachmentIconButtonContainer: {
            margin: '0 15px 0 24px',
            border: '2px solid #F0F2F6',
            borderRadius: '100%',
        },
        attachmentIconButton: {
            width: '18px',
            height: '18px',
            color: 'rgba(17, 30, 20, 0.4)',
            '&:hover': {
                backgroundColor: '#EBE9E9',
            },
        },
        replyMessage: {
            flexGrow: 1,
            marginRight: '8px',
        },
        replyInputRoot: {
            '&$replyInputFocused $replyInputNotchedOutline': {
                borderColor: `${theme.palette.secondary.main} !important`,
            },
        },
        replyInputFocused: {},
        replyInputNotchedOutline: {
            borderWidth: '2px',
            borderColor: '#D5D7DD !important',
        },
        replyInput: {
            fontSize: '16px',
        },
        replyInputMultiline: {
            padding: '14px',
        },
        sendButton: {
            height: '46px',
            [theme.breakpoints.up('xl')]: { width: '118px' },
        },
        // .chatRightBlock3{padding: 48px 0 21px 0;}
        // .chatRightBlock3 a{}
        // .chatRightBlock3 input{float: left;display: inline-block;width: 379px;height: 48px;line-height: 48px;border: 2px solid #eaebef;border-radius: 5px;font-size:16px;color: #a0a5a1;padding: 0 14px;margin: 0 6px 0 0;}
        // .chatRightBlock3 input:focus{border: 2px solid #1d8a8b !important;outline: none;}
        // .chatRightBlock3 button{width: 118px;height: 48px;line-height: 48px;background: #f1f2f6;border-radius: 5px;font-size: 15px;color: #979d9d;font-weight: 500;text-align: left;cursor: pointer;border: 0px;}
        // .chatRightBlock3 button img{float: left;margin: 11px 8px 0 23px;}
        // .chatRightBlock3 button:hover{background: #1d8a8b;color: #ffffff;}
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, props, message, currentThreadId, draftReplies, selectedFile, fileInputRef } = params;
    const { setMessage, clearReply, setSelectedFile } = params;
    const { disabled, onMessageSend, onFileUploadComplete, setIsRequestScrollToBottomRef } = props;
    const {
        replyInputRoot: replyInputRootCls,
        replyInput: replyInputCls,
        replyInputFocused: replyInputFocusedCls,
        replyInputNotchedOutline: replyInputNotchedOutlineCls,
        replyInputMultiline: replyInputMultilineCls,
        sendButton: sendButtonCls,
    } = classes;

    const isEmptyMessage = _isNil(message) || _trim(message) === '';
    const sendButtonDisabled = disabled || isEmptyMessage;
    const attachmentIconButtonDisabled = disabled;
    const attachmentDialogOpen = !!selectedFile;

    const defns = {
        threadDeactivatedMessageText: { clsGrayColor: true, clsLightBold: false, value: THIS_THREAD_HAS_BEEN_DEACTIVATED },
        replyMessage: {
            fullWidth: true,
            variant: 'outlined',
            // size: 'small',
            multiline: true,
            placeholder: 'Type your message...',
            InputProps: {
                // readOnly: !isEnabled,
                classes: {
                    root: replyInputRootCls,
                    focused: replyInputFocusedCls,
                    notchedOutline: replyInputNotchedOutlineCls,
                    input: replyInputCls,
                    multiline: replyInputMultilineCls,
                },
            },
            value: message,
            // inputProps: {
            //     maxLength: 200,
            // },
            // FormHelperTextProps: {
            //     className: noteHelperTextCls,
            // },
            onKeyPress: handleKeyUpReplyMessage.bind(null, onMessageSend, clearReply, setIsRequestScrollToBottomRef),
            onChange: handleChangeReplyMessage.bind(null, setMessage, currentThreadId, draftReplies),
            disabled,
        },
        sendButton: {
            classes: {
                root: sendButtonCls,
            },
            color: 'secondary',
            disabled: sendButtonDisabled,
            onClick: handleClickSendButton.bind(null, onMessageSend, message, clearReply, setIsRequestScrollToBottomRef),
        },
        attachmentToolTip: {
            title: 'Upload Attachment',
            placement: 'top',
            arrow: true,
        },
        attachmentIconButton: {
            component: 'label',
            disabled: attachmentIconButtonDisabled,
        },
        attachmentDialog: {
            open: attachmentDialogOpen,
            onClose: handleCloseAttachmentDialog.bind(null, setSelectedFile),
            onAttachmentUploadComplete: handleCompleteAttachmentUpload.bind(null, onFileUploadComplete, clearReply, setIsRequestScrollToBottomRef),
            selectedFile,
            allowedFileTypes,
            message,
            // setMessage,
            onMessageSend,
            clearReply,
            fileInputRef,
        },
        selectFileInput: {
            type: 'file',
            style: { display: 'none' },
            onChange: handleChangeFile.bind(null, setSelectedFile),
            accept: allowedFileTypes.join(', '),
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const allowedFileTypes = ['.png', '.jpg', '.jpeg', '.pdf', '.doc', '.docx', '.docm', '.xls', '.xlsx', 'xlsm'];

const handleChangeFile = (setSelectedFile, evt) => {
    const selectedFile = _get(evt, 'target.files[0]');
    setSelectedFile(selectedFile);
};

const handleCloseAttachmentDialog = setSelectedFile => {
    setSelectedFile(null);
};

const handleCompleteAttachmentUpload = (onFileUploadComplete, clearReply, setIsRequestScrollToBottomRef, cleanUpDialog, message, file, url) => {
    const { name, type, size } = file;
    clearReply(); // clear reply in the ThreadDetail pane
    onFileUploadComplete(message, name, type, size, url); // file onFileUploadComplete event
    cleanUpDialog(); // dialog clean up
    setIsRequestScrollToBottomRef(true); // scroll to bottom of the thread
};

const handleClickSendButton = (onMessageSend, message, clearReply, setIsRequestScrollToBottomRef, evt) => {
    clearReply();
    onMessageSend(message);
    setIsRequestScrollToBottomRef(true);
};

const handleChangeReplyMessage = (setMessage, currentThreadId, draftReplies, evt) => {
    setMessage(evt.target.value);
    // also store the draft reply message so we can restore it and display in Thread Summaries
    _set(draftReplies, `current.${currentThreadId}`, evt.target.value);
};

const handleKeyUpReplyMessage = (onMessageSend, clearReply, setIsRequestScrollToBottomRef, evt) => {
    if (evt.key === 'Enter' && !evt.shiftKey) {
        evt.preventDefault();
        const message = evt.target.value;
        if (!_isNil(message) && _trim(message) !== '') {
            clearReply();
            onMessageSend(evt.target.value);
            setIsRequestScrollToBottomRef(true);
        }
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const MessageReply = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const context = useContext(LayoutContext);
    const appContext = useContext(AppContext);
    const draftReplies = _get(appContext, 'draftReplies');
    const currentThreadId = _get(context, 'currentThreadId');
    const fileInputRef = useRef();

    const theme = useTheme();
    const lgMatches = useMediaQuery(theme.breakpoints.down('lg'));

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [message, setMessage] = useState(_get(draftReplies, `current.${currentThreadId}`, ''));
    const [selectedFile, setSelectedFile] = useState();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { disabled } = props;

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const clearReply = useCallback(() => {
        // clear the reply field and the draft
        setMessage('');
        _set(draftReplies, `current.${currentThreadId}`, '');
    }, [currentThreadId, draftReplies, setMessage]);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        setMessage(_get(draftReplies, `current.${currentThreadId}`, ''));
    }, [draftReplies, currentThreadId]);

    useEffect(() => {
        if (disabled) {
            clearReply();
        }
    }, [disabled, clearReply]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, message, setMessage, clearReply, currentThreadId, draftReplies, selectedFile, setSelectedFile, fileInputRef });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    if (disabled) {
        return (
            <div {...defns.root}>
                <div {...defns.blockMessageContainer}>
                    <AfitdTypography {...defns.threadDeactivatedMessageText} />
                </div>
            </div>
        );
    } else {
        return (
            <div {...defns.root}>
                <div {...defns.attachmentIconButtonContainer}>
                    <Tooltip {...defns.attachmentToolTip}>
                        <IconButton {...defns.attachmentIconButton}>
                            <AttachmentIcon />
                            <input {...defns.selectFileInput} ref={fileInputRef} />
                        </IconButton>
                    </Tooltip>
                </div>
                <FileReplyDialog {...defns.attachmentDialog} />
                <AfitdTextField {...defns.replyMessage} />
                {lgMatches ? (
                    <AfitdButton {...defns.sendButton}>
                        <SendIcon />
                    </AfitdButton>
                ) : (
                    // <AfitdIconButton {...defns.sendButton}>
                    //     <SendIcon />
                    // </AfitdIconButton>
                    <AfitdButton startIcon={<SendIcon />} {...defns.sendButton}>
                        Send
                    </AfitdButton>
                )}
            </div>
        );
    }
};

export default MessageReply;
